import { useEffect, useState } from 'react';
import { ManagementBar } from '@/components/animate-ui/components/community/management-bar';
import logo from '../assets/images/logo.png'
import { useAuth } from '../context/AuthContext';
import { AuthModal } from './AuthModal';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface HeaderProps {
  currentPage?: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering" | "about" | "socials" | "dashboard";
  onNavigate?: (page: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering" | "about" | "socials" | "dashboard", targetId?: string) => void;
}

export default function Header({ currentPage, onNavigate }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "signup">("login");
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    const handleOpenAuth = (e: Event) => {
      const customEvent = e as CustomEvent;
      const tab = customEvent.detail?.tab || "login";
      setAuthModalTab(tab);
      setIsAuthModalOpen(true);
    };

    window.addEventListener("open-auth-modal", handleOpenAuth);
    return () => window.removeEventListener("open-auth-modal", handleOpenAuth);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 15) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? "bg-[#05030a]/80 backdrop-blur-xl border-b border-white/10 shadow-xl py-3" 
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto flex items-center justify-between gap-4 px-6 md:px-12">

        {/* Left Side: Logo */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => onNavigate?.("home")}
            className="relative group text-left cursor-pointer focus:outline-none"
            aria-label="Home"
          >
            <div className="relative h-11 w-36 overflow-hidden group/headerlogo">
              {/* Soft organic neon glow behind the logo */}
              <div className="absolute inset-0 bg-violet-600/35 blur-lg rounded-full scale-x-[0.8] scale-y-[0.4] pointer-events-none z-0" />

              {/* HD Base Logo with Dynamic Glow and Screen Blending */}
              <img
                src={logo}
                alt="Wesitex Logo"
                className="absolute inset-0 h-full w-full object-contain scale-[1.8] translate-y-[-12%] mix-blend-screen z-10"
                style={{ imageRendering: '-webkit-optimize-contrast' }}
              />

              {/* Metallic Shine Reflection Masked to the logo shape with Screen Blending */}
              <div 
                className="absolute inset-0 h-full w-full scale-[1.8] translate-y-[-12%] z-20 pointer-events-none mix-blend-screen"
                style={{
                  WebkitMaskImage: `url(${logo})`,
                  maskImage: `url(${logo})`,
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                }}
              >
                {/* Auto-looping Shine Streak */}
                <div className="absolute inset-0 w-[150%] h-full sync-shine-streak bg-linear-to-r from-transparent via-white/80 to-transparent mix-blend-overlay" />
              </div>
            </div>
          </button>
        </div>

        {/* Center: Management Bar as Navigation */}
        <div className="flex-1 hidden lg:flex justify-center">
          <div className="w-full max-w-2xl">
            <ManagementBar onNavigate={onNavigate} />
          </div>
        </div>

        {/* Right Side: CTA / Auth (Desktop) */}
        <div className="shrink-0 hidden lg:flex items-center gap-4">
          {isAuthenticated && user ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigate?.("dashboard")}
                className="mr-2 text-xs font-mono font-bold tracking-wider text-brand-electric bg-brand-purple/10 border border-brand-purple/30 hover:bg-brand-purple/20 px-3.5 py-1.5 rounded-xl active:scale-95 transition-all cursor-pointer focus:outline-none"
              >
                {user.role === "superadmin" ? "Admin Console" : "My Dashboard"}
              </button>
              {user.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.name} 
                  className="w-9 h-9 rounded-full border border-brand-purple/50 shadow-[0_0_10px_rgba(139,92,246,0.3)] object-cover" 
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-brand-purple/20 border border-brand-purple/50 flex items-center justify-center text-brand-electric font-semibold text-sm">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-400">Welcome,</span>
                <span className="text-xs font-semibold text-white max-w-[80px] truncate">{user.name}</span>
              </div>
              <button
                onClick={logout}
                className="text-xs font-semibold text-slate-400 hover:text-white hover:underline transition-colors ml-2 cursor-pointer focus:outline-none"
              >
                Log Out
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => {
                  setAuthModalTab("login");
                  setIsAuthModalOpen(true);
                }}
                className="text-sm font-semibold text-white hover:text-brand-electric transition-colors cursor-pointer focus:outline-none mr-2"
              >
                Sign In
              </button>
              <button
                onClick={() => onNavigate?.("contact")}
                className="rounded-xl bg-brand-purple px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-purple/90 transition-all shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:scale-105 active:scale-95 cursor-pointer focus:outline-none"
              >
                Contact Us
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex lg:hidden items-center">
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-white hover:text-brand-electric transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 w-full bg-[#05030a]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl py-4 px-6 flex flex-col gap-4 lg:hidden z-40"
          >
            <button 
              onClick={() => { onNavigate?.("home", "industries"); setIsMobileMenuOpen(false); }}
              className="text-left py-2 text-white hover:text-brand-electric font-medium border-b border-white/5"
            >
              Services Provided
            </button>
            <button 
              onClick={() => { onNavigate?.("about"); setIsMobileMenuOpen(false); }}
              className="text-left py-2 text-white hover:text-brand-electric font-medium border-b border-white/5"
            >
              Who Wesitex ?
            </button>
            <button 
              onClick={() => { onNavigate?.("socials"); setIsMobileMenuOpen(false); }}
              className="text-left py-2 text-white hover:text-brand-electric font-medium border-b border-white/5"
            >
              Social Media
            </button>
            <button 
              onClick={() => { onNavigate?.("home", "products"); setIsMobileMenuOpen(false); }}
              className="text-left py-2 text-brand-electric font-bold border-b border-white/5"
            >
              View: Our Software
            </button>
            
            <div className="pt-2 flex flex-col gap-3">
              {isAuthenticated && user ? (
                <>
                  <button
                    onClick={() => { onNavigate?.("dashboard"); setIsMobileMenuOpen(false); }}
                    className="w-full py-2.5 rounded-xl bg-brand-purple/20 border border-brand-purple/50 text-brand-electric font-bold"
                  >
                    {user.role === "superadmin" ? "Admin Console" : "My Dashboard"}
                  </button>
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    className="w-full py-2.5 rounded-xl bg-white/5 text-white font-medium hover:bg-white/10"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setAuthModalTab("login");
                      setIsAuthModalOpen(true);
                    }}
                    className="w-full py-2.5 rounded-xl bg-white/5 text-white font-medium hover:bg-white/10"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => { onNavigate?.("contact"); setIsMobileMenuOpen(false); }}
                    className="w-full py-2.5 rounded-xl bg-brand-purple text-white font-bold shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                  >
                    Contact Us
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialTab={authModalTab} />
    </header>
  );
}
